import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

const sizeClass = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-5',
} as const;

export type SpinnerSize = keyof typeof sizeClass;

export interface SpinnerProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  size?: SpinnerSize;
  /**
   * Text announced to screen readers. Pass `null` when the spinner sits inside
   * something that already announces busy state (e.g. a loading Button), so the
   * status is not read out twice.
   */
  label?: string | null;
}

export function Spinner({ size = 'md', label = 'Loading', className, ...rest }: SpinnerProps) {
  const decorative = label === null;

  return (
    <span
      {...rest}
      className={cn('inline-flex items-center justify-center', className)}
      role={decorative ? undefined : 'status'}
      aria-hidden={decorative ? true : undefined}
    >
      <svg
        // Motion here is meaningful, so it survives prefers-reduced-motion —
        // see the guard in theme.css. It only slows down.
        data-wend-motion="essential"
        className={cn('animate-spin motion-reduce:[animation-duration:1.8s]', sizeClass[size])}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.75" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          strokeWidth="2.75"
          strokeLinecap="round"
        />
      </svg>
      {!decorative && <span className="sr-only">{label}</span>}
    </span>
  );
}
